import React, { useEffect, useState } from "react";

import {
  Key,
  Mail,
  Lock,
  Unlock,
  User,
  Trash2,
  SquarePen,
  Plus,
} from "lucide-react";

import AddUserModal from "./AddUserModal";
import ResetPasswordModal from "./ResetPasswordModal";
import SendInvitationModal from "./SendInvitationModal";
import ToggleUserStatusModal from "./ToggleUserStatusModal";
import Button from "../../../Components/Button";
import DropdownSelector from "../../../Components/DropdownSelector";
import CommonModal from "../../../Components/Modal";
import Pagination from "../../../Components/Pagination";
import SearchInput from "../../../Components/Search/SearchInput";
import SuccessNotification from "../../../Components/SuccessNotification";
import Table from "../../../Components/Table";
import ToggleSwitch from "../../../Components/ToggleSwitch";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import { fetchRoles } from "../../../Store/Reducers/MasterSlice";
import {
  fetchVendorAdminUsers,
  deleteVendorUser,
  resetVendorUserPassword,
  updateVendorUser,
  createVendorUser,
  resendWelcomeEmail,
  fetchWeeklyDigestStatus,
  clearDeleteUserState,
  clearUpdateUserState,
  clearCreateUserState,
  clearResendWelcomeEmailState,
} from "../../../Store/Reducers/VendorAdminSlice";
import {
  ACCOUNT_TEAM_ROLE_FILTER,
  ROLE_DISPLAY_NAMES,
  ROLES,
  isAccountTeamRole,
  normalizeRoleName,
  RoleType,
} from "../../../Utils/Constants/RoleConstants";
import { formatDateUS } from "../../../Utils/Helper/Helper";
import DeleteModal from "../../DeleteModal";

import "./ToggleUserStatusModal/style.scss";

interface User {
  user_id?: string;
  id?: string;
  name?: string;
  full_name?: string;
  user_name?: string;
  email: string;
  role_name: string;
  role_label?: string;
  role?: {
    name?: string;
    label?: string;
  };
  status: string;
  created_at: string;
  last_contacted_at?: string;
  last_login_at?: string;
  position?: string;
  phone?: string;
  city?: string;
  country?: string;
  join_date?: string;
  secondary_role?: string;
  executive_ops?: boolean;
  executive_ops_approve?: boolean;
  weekly_digest_email?: boolean;
}

interface Props {
  onSendEmail?: (id: string) => void;
}

const getUserId = (user: User | null | undefined): string | undefined =>
  user ? (user.user_id ?? user.id) : undefined;

const getUserDisplayName = (user: User | null | undefined): string =>
  user
    ? (user.name ??
      user.full_name ??
      user.user_name ??
      user.email ??
      "this user")
    : "";

const isExecutiveSponsor = (roleName: string | undefined): boolean =>
  normalizeRoleName(roleName) === "executive_sponsor";

const isExecutiveRole = (roleName: string | undefined): boolean =>
  normalizeRoleName(roleName) === ROLES.EXECUTIVE;

const getAccessToggleState = (
  record: User,
  field: "executive_ops" | "executive_ops_approve"
): { checked: boolean; disabled: boolean; title?: string } => {
  const isExecSponsor = isExecutiveSponsor(record.role_name);
  const isAccountTeam = isAccountTeamRole(record.role_name);
  const isExecutive = isExecutiveRole(record.role_name);

  if (isExecSponsor) {
    return {
      checked:
        field === "executive_ops"
          ? (record.executive_ops ?? false)
          : (record.executive_ops_approve ?? false),
      disabled: false,
    };
  }

  if (isAccountTeam || isExecutive) {
    return {
      checked: false,
      disabled: true,
      title: isExecutive
        ? "Access toggles are not available for Executive role"
        : "Access toggles are not available for account team roles",
    };
  }

  return {
    checked: true,
    disabled: true,
    title: "This role has full access",
  };
};

const getWeeklyDigestToggleState = (
  record: User,
  digestAvailable: boolean
): { checked: boolean; disabled: boolean; title?: string } => {
  if (!digestAvailable) {
    if (!isExecutiveSponsor(record.role_name)) {
      return {
        checked: false,
        disabled: true,
        title: "Weekly Digest applies to executive sponsors only",
      };
    }
    return {
      checked: record.weekly_digest_email === true,
      disabled: true,
      title: "Weekly Digest is not enabled for this organization",
    };
  }

  if (!isExecutiveSponsor(record.role_name)) {
    return {
      checked: false,
      disabled: true,
      title: "Weekly Digest applies to executive sponsors only",
    };
  }

  return {
    checked: record.weekly_digest_email === true,
    disabled: false,
  };
};

const VendorUserManagementContainer: React.FC<Props> = ({ onSendEmail }) => {
  const dispatch = useAppDispatch();
  const {
    loading: usersLoading,
    usersResponse,
    deleteUserLoading,
    deleteUserSuccess,
    deleteUserError,
    resetPasswordLoading,
    updateUserLoading,
    updateUserError,
    createUserLoading,
    createUserSuccess,
    createUserError,
    resendWelcomeEmailLoading,
    resendWelcomeEmailSuccess,
    resendWelcomeEmailError,
    weeklyDigestStatus,
  } = useAppSelector((state: any) => state.vendorAdmin);
  const { roles } = useAppSelector((state: any) => state.master);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false);
  const [toggleStatusModalOpen, setToggleStatusModalOpen] = useState(false);
  const [toggleAccessModalOpen, setToggleAccessModalOpen] = useState(false);
  const [toggleWeeklyDigestModalOpen, setToggleWeeklyDigestModalOpen] =
    useState(false);
  const [pendingWeeklyDigestToggle, setPendingWeeklyDigestToggle] = useState<{
    user: User;
    newValue: boolean;
  } | null>(null);
  const [pendingAccessToggle, setPendingAccessToggle] = useState<{
    user: User;
    field: "executive_ops" | "executive_ops_approve";
    newValue: boolean;
  } | null>(null);
  const [sendInvitationModalOpen, setSendInvitationModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [notification, setNotification] = useState<{
    show: boolean;
    type: "success" | "error";
    message: string;
    details: string;
  }>({ show: false, type: "success", message: "", details: "" });

  useEffect(() => {
    if (!roles.items || roles.items.length === 0) {
      dispatch(fetchRoles() as any);
    }
  }, [dispatch, roles.items]);

  useEffect(() => {
    dispatch(fetchWeeklyDigestStatus() as any);
  }, [dispatch]);

  const weeklyDigestAvailable =
    weeklyDigestStatus?.org_entitled !== false &&
    weeklyDigestStatus?.org_active !== false;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setCurrentPage(1); // Reset to first page on search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);

  const buildUserListParams = React.useCallback(() => {
    const params: {
      page?: number;
      page_size?: number;
      search?: string;
      role?: string;
    } = {
      page: currentPage,
      page_size: pageSize,
    };
    if (debouncedSearch.trim()) {
      params.search = debouncedSearch.trim();
    }
    if (selectedRole) {
      params.role = selectedRole;
    }
    return params;
  }, [currentPage, pageSize, debouncedSearch, selectedRole]);

  const refetchUserList = React.useCallback(() => {
    return dispatch(fetchVendorAdminUsers(buildUserListParams()) as any);
  }, [dispatch, buildUserListParams]);

  // Fetch users when pagination, search, or role changes
  useEffect(() => {
    dispatch(fetchVendorAdminUsers(buildUserListParams()) as any);
  }, [dispatch, buildUserListParams]);

  useEffect(() => {
    if (!deleteUserSuccess) {
      return;
    }
    setDeleteUserModalOpen(false);
    dispatch(clearDeleteUserState());
    refetchUserList();
    setNotification({
      show: true,
      type: "success",
      message: "User Deleted Successfully",
      details: "The user has been deleted successfully.",
    });
  }, [deleteUserSuccess, dispatch, refetchUserList]);

  useEffect(() => {
    if (deleteUserError) {
      setNotification({
        show: true,
        type: "error",
        message: "Failed to Delete User",
        details:
          deleteUserError ||
          "An error occurred while deleting the user. Please try again.",
      });
    }
  }, [deleteUserError]);

  useEffect(() => {
    if (updateUserError) {
      const errorMessage =
        updateUserError ||
        "An error occurred while updating the user. Please try again.";
      setNotification({
        show: true,
        type: "error",
        message: "Failed to Update User",
        details: errorMessage,
      });
    }
  }, [updateUserError]);

  useEffect(() => {
    if (!createUserSuccess) {
      return;
    }
    setAddUserModalOpen(false);
    dispatch(clearCreateUserState());
    refetchUserList();
    setNotification({
      show: true,
      type: "success",
      message: "User Created Successfully",
      details: "The user has been created successfully.",
    });
  }, [createUserSuccess, dispatch, refetchUserList]);

  useEffect(() => {
    if (createUserError) {
      setNotification({
        show: true,
        type: "error",
        message: "Failed to Create User",
        details:
          createUserError ||
          "An error occurred while creating the user. Please try again.",
      });
    }
  }, [createUserError]);

  useEffect(() => {
    if (resendWelcomeEmailSuccess) {
      setSendInvitationModalOpen(false);
      dispatch(clearResendWelcomeEmailState());
      setNotification({
        show: true,
        type: "success",
        message: "Invitation Sent Successfully",
        details: "The invitation email has been sent successfully.",
      });
    }
  }, [resendWelcomeEmailSuccess, dispatch]);

  useEffect(() => {
    if (resendWelcomeEmailError) {
      setNotification({
        show: true,
        type: "error",
        message: "Failed to Send Invitation",
        details:
          resendWelcomeEmailError ||
          "An error occurred while sending the invitation. Please try again.",
      });
    }
  }, [resendWelcomeEmailError]);

  const handleResetPasswordClick = (user: User) => {
    setSelectedUser(user);
    setResetPasswordModalOpen(true);
  };

  const handleResetPassword = async () => {
    const userId = getUserId(selectedUser);
    if (userId) {
      await dispatch(
        resetVendorUserPassword({
          user_id: userId,
        }) as any
      );
    }
    setResetPasswordModalOpen(false);
  };

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setIsEditMode(true);
    setAddUserModalOpen(true);
  };

  const handleEditSubmit = async (data: any) => {
    const userId = getUserId(selectedUser);
    if (isEditMode && userId) {
      const isAccountTeam = isAccountTeamRole(data.role);
      const result = await dispatch(
        updateVendorUser({
          user_id: userId,
          name: data.fullName,
          email: data.email,
          role_name: data.role,
          phone: data.phone,
          position: data.position,
          ...(isAccountTeam
            ? { secondary_role: data.languages?.trim() || undefined }
            : {}),
        })
      );
      if (result.meta?.requestStatus === "fulfilled") {
        setToggleStatusModalOpen(false);
        setAddUserModalOpen(false);
        dispatch(clearUpdateUserState());
        refetchUserList();
        setNotification({
          show: true,
          type: "success",
          message: "User Updated Successfully",
          details: "The user has been updated successfully.",
        });
      }
    } else {
      dispatch(
        createVendorUser({
          email: data.email,
          name: data.fullName,
          role_name: data.role,
          position: data.position,
          phone: data.phone,
          city: data.city,
          country: data.country,
          join_date: data.join_date,
          send_invitation: data.send_invitation,
        })
      );
    }
  };

  const handleToggleStatusClick = (user: User) => {
    setSelectedUser(user);
    setToggleStatusModalOpen(true);
  };

  const handleToggleStatusConfirm = async () => {
    const userId = getUserId(selectedUser);
    if (userId) {
      const result = await dispatch(
        updateVendorUser({
          user_id: userId,
          status: selectedUser!.status === "active" ? "inactive" : "active",
        })
      );
      if (result.meta?.requestStatus === "fulfilled") {
        setToggleStatusModalOpen(false);
        dispatch(clearUpdateUserState());
        refetchUserList();
        setNotification({
          show: true,
          type: "success",
          message: "User Status Updated Successfully",
          details: "The user status has been updated successfully.",
        });
      }
    }
  };

  const handleToggleAccessConfirm = async () => {
    const userId = getUserId(pendingAccessToggle?.user);
    if (!userId) return;
    const { field, newValue, user } = pendingAccessToggle!;

    // When enabling approver access without exec ops, enable both
    const enableBoth =
      field === "executive_ops_approve" &&
      newValue === true &&
      !(user.executive_ops === true);

    // When disabling approver access, also disable exec ops
    const disableBothOnApprover =
      field === "executive_ops_approve" && newValue === false;

    // When disabling exec ops access, also disable approver access
    const disableBothOnExecOps =
      field === "executive_ops" && newValue === false;

    const payload: {
      user_id: string;
      executive_ops?: boolean;
      executive_ops_approve?: boolean;
    } = {
      user_id: userId,
    };

    if (field === "executive_ops") {
      payload.executive_ops = newValue === true;
      if (disableBothOnExecOps) {
        payload.executive_ops_approve = false;
      }
    } else {
      payload.executive_ops_approve = newValue === true;
      if (enableBoth) {
        payload.executive_ops = true;
      } else if (disableBothOnApprover) {
        payload.executive_ops = false;
      }
    }

    const result = await dispatch(updateVendorUser(payload));
    setToggleAccessModalOpen(false);
    setPendingAccessToggle(null);
    if (result.meta?.requestStatus === "fulfilled") {
      dispatch(clearUpdateUserState());
      refetchUserList();
      setNotification({
        show: true,
        type: "success",
        message: "Access Updated Successfully",
        details: enableBoth
          ? "Exec Ops Access and Approver Access have been enabled successfully."
          : "The access permission has been updated successfully.",
      });
    }
  };

  const handleToggleAccessCancel = () => {
    setToggleAccessModalOpen(false);
    setPendingAccessToggle(null);
  };

  const handleToggleWeeklyDigestConfirm = async () => {
    const userId = getUserId(pendingWeeklyDigestToggle?.user);
    if (!userId || !pendingWeeklyDigestToggle) return;

    const result = await dispatch(
      updateVendorUser({
        user_id: userId,
        weekly_digest_email: pendingWeeklyDigestToggle.newValue,
      })
    );
    setToggleWeeklyDigestModalOpen(false);
    setPendingWeeklyDigestToggle(null);
    if (result.meta?.requestStatus === "fulfilled") {
      dispatch(clearUpdateUserState());
      refetchUserList();
      setNotification({
        show: true,
        type: "success",
        message: "Weekly Digest Updated Successfully",
        details: pendingWeeklyDigestToggle.newValue
          ? "This user will receive weekly digest emails."
          : "This user will no longer receive weekly digest emails.",
      });
    }
  };

  const handleToggleWeeklyDigestCancel = () => {
    setToggleWeeklyDigestModalOpen(false);
    setPendingWeeklyDigestToggle(null);
  };

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setDeleteUserModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    const userId = getUserId(selectedUser);
    if (userId) {
      dispatch(deleteVendorUser(userId));
    }
  };

  const handleSendEmailClick = (user: User) => {
    setSelectedUser(user);
    setSendInvitationModalOpen(true);
  };

  const handleSendInvitationConfirm = () => {
    const userId = getUserId(selectedUser);
    if (userId) {
      dispatch(resendWelcomeEmail(userId) as any);
    }
  };

  const getStatusBadgeClasses = (status: string) => {
    if (status === "active") {
      return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 capitalize";
    }
    if (status === "inactive") {
      return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 capitalize";
    }
    return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 capitalize";
  };
  const [selectedUser, setSelectedUser] = React.useState<User | null>(null);

  // Helper function to transform roles to options format
  const transformRolesToOptions = React.useCallback(
    (rolesList: { role_id: string; name: string; label?: string }[]) => {
      return rolesList.map(
        (role: { role_id: string; name: string; label?: string }) => {
          const displayName = role.label || role.name;
          return {
            label: displayName,
            value: role.name,
          };
        }
      );
    },
    []
  );

  // Role options for create/edit user — exclude sponsor, executive, and account team roles
  const roleOptions = React.useMemo(() => {
    if (!roles.items || roles.items.length === 0) {
      return [];
    }
    const filteredRoles = roles.items.filter(
      (role: { role_id: string; name: string; label?: string }) => {
        const normalized = normalizeRoleName(role.name);
        return (
          normalized !== ROLES.EXECUTIVE_SPONSOR &&
          normalized !== ROLES.EXECUTIVE &&
          !isAccountTeamRole(role.name)
        );
      }
    );
    return transformRolesToOptions(filteredRoles);
  }, [roles.items, transformRolesToOptions]);

  const accountTeamRoleOptions = React.useMemo(() => {
    if (!roles.items?.length) return [];
    return transformRolesToOptions(
      roles.items.filter(
        (role: { role_id: string; name: string; label?: string }) =>
          isAccountTeamRole(role.name)
      )
    );
  }, [roles.items, transformRolesToOptions]);

  // Role filter dropdown — one "Account Team" option instead of individual AE/CSM/… roles
  const roleFilterOptions = React.useMemo(() => {
    const options: { label: string; value: string }[] = [
      { label: "Account Team", value: ACCOUNT_TEAM_ROLE_FILTER },
    ];
    if (!roles.items?.length) {
      return options;
    }
    const nonAccountTeamRoles = roles.items.filter(
      (role: { role_id: string; name: string; label?: string }) =>
        !isAccountTeamRole(role.name)
    );
    return [...options, ...transformRolesToOptions(nonAccountTeamRoles)];
  }, [roles.items, transformRolesToOptions]);
  const columns = [
    {
      key: "name",
      title: "NAME",
      render: (_: any, record: User) => (
        <div className="flex flex-wrap-cell items-center gap-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <User className="w-5 h-5 text-purple-600" />
            </div>
          </div>
          <div className="cell-info">
            <div className="text-sm font-medium text-gray-900">
              {getUserDisplayName(record)}
            </div>
            <div className="text-sm text-gray-500">{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: "role_name",
      title: "ROLE",
      render: (value: string) => {
        const matchedRole = roles.items?.find(
          (role: { name: string; label?: string }) => role.name === value
        );
        const apiRoleLabel = matchedRole?.label;
        const normalizedRole = normalizeRoleName(value);
        const fallbackDisplayName =
          normalizedRole && normalizedRole in ROLE_DISPLAY_NAMES
            ? ROLE_DISPLAY_NAMES[normalizedRole as RoleType]
            : value;
        return <span>{apiRoleLabel || fallbackDisplayName}</span>;
      },
    },
    {
      key: "executive_ops",
      title: "EXEC OPS ACCESS",
      render: (_: unknown, record: User) => {
        const toggleState = getAccessToggleState(record, "executive_ops");
        return (
          <span title={toggleState.title} className="inline-block">
            <ToggleSwitch
              checked={toggleState.checked}
              onChange={checked => {
                setPendingAccessToggle({
                  user: record,
                  field: "executive_ops",
                  newValue: checked === true,
                });
                setToggleAccessModalOpen(true);
              }}
              id={`executive_ops-${getUserId(record) ?? record.email ?? ""}`}
              disabled={updateUserLoading || toggleState.disabled}
            />
          </span>
        );
      },
    },
    {
      key: "executive_ops_approve",
      title: "APPROVER ACCESS",
      render: (_: unknown, record: User) => {
        const toggleState = getAccessToggleState(
          record,
          "executive_ops_approve"
        );
        return (
          <span title={toggleState.title} className="inline-block">
            <ToggleSwitch
              checked={toggleState.checked}
              onChange={checked => {
                setPendingAccessToggle({
                  user: record,
                  field: "executive_ops_approve",
                  newValue: checked === true,
                });
                setToggleAccessModalOpen(true);
              }}
              id={`executive_ops_approve-${getUserId(record) ?? record.email ?? ""}`}
              disabled={updateUserLoading || toggleState.disabled}
            />
          </span>
        );
      },
    },
    {
      key: "weekly_digest_email",
      title: "WEEKLY DIGEST",
      render: (_: unknown, record: User) => {
        const toggleState = getWeeklyDigestToggleState(
          record,
          weeklyDigestAvailable
        );
        return (
          <span title={toggleState.title} className="inline-block">
            <ToggleSwitch
              checked={toggleState.checked}
              onChange={checked => {
                setPendingWeeklyDigestToggle({
                  user: record,
                  newValue: checked === true,
                });
                setToggleWeeklyDigestModalOpen(true);
              }}
              id={`weekly_digest-${getUserId(record) ?? record.email ?? ""}`}
              disabled={updateUserLoading || toggleState.disabled}
            />
          </span>
        );
      },
    },
    {
      key: "status",
      title: "STATUS",
      render: (value: string) => (
        <span className={getStatusBadgeClasses(value)}>{value}</span>
      ),
    },
    {
      key: "last_contacted_at",
      title: "LAST CONTACTED",
      render: (_: unknown, record: User) => (
        <span>
          {record.last_contacted_at
            ? formatDateUS(record.last_contacted_at)
            : "-"}
        </span>
      ),
    },
    {
      key: "last_login_at",
      title: "LAST LOGIN",
      render: (_: unknown, record: User) => (
        <span>
          {record.last_login_at ? formatDateUS(record.last_login_at) : "-"}
        </span>
      ),
    },
    {
      key: "actions",
      title: "ACTIONS",
      textalign: "center",
      className: "flex justify-center",
      render: (_: any, record: User) => (
        <div className="flex gap-1 w-full justify-center">
          <Button
            variant="clear"
            onClick={() => handleResetPasswordClick(record)}
            title="Reset Password"
          >
            <Key className="text-blue-500" height={18} />
          </Button>
          <Button
            variant="clear"
            onClick={() => handleSendEmailClick(record)}
            title="Send Email"
          >
            <Mail className="text-green-500" height={18} />
          </Button>
          <Button
            variant="clear"
            onClick={() => handleEditClick(record)}
            title="Edit"
          >
            <SquarePen className="text-orange-500" height={18} />
          </Button>
          <Button
            variant="clear"
            onClick={() => handleToggleStatusClick(record)}
            title={
              record.status === "active" ? "Deactivate User" : "Activate User"
            }
          >
            {record.status === "active" ? (
              <Lock className="text-orange-600" height={18} />
            ) : (
              <Unlock className="text-orange-600" height={18} />
            )}
          </Button>
          <Button
            variant="clear"
            onClick={() => handleDeleteClick(record)}
            title="Delete"
          >
            <Trash2 className="text-red-500" height={18} />
          </Button>
        </div>
      ),
    },
  ];
  const handleRoleFilterChange = (role: string) => {
    setCurrentPage(1);
    setSelectedRole(role);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h1 className="font-semibold text-lg">User Management</h1>
        <div className="flex items-center gap-3">
          <DropdownSelector
            label="Select Role"
            selected={selectedRole}
            options={[
              { label: "Select Role", value: "" },
              ...roleFilterOptions,
            ]}
            onSelect={handleRoleFilterChange}
            enableScroll={true}
          />
          <SearchInput
            placeholder="Search users..."
            value={searchValue}
            onChange={e => setSearchValue?.(e.target.value)}
          />
          <Button
            variant="primary"
            className="whitespace-nowrap flex-shrink-0 flex items-center gap-2"
            onClick={() => {
              setSelectedUser(null);
              setIsEditMode(false);
              setAddUserModalOpen(true);
            }}
          >
            <Plus size={18} />
            Add User
          </Button>
        </div>
      </div>

      <Table
        columns={columns as any}
        data={usersResponse?.users || []}
        loading={usersLoading}
        // scrollable={true}
        pagination={false}
        // maxHeight="calc(-21rem + 100vh)"
      />

      {/* Server-Side Pagination */}
      {usersResponse && usersResponse.total > 0 && (
        <Pagination
          mode="dynamic"
          currentPage={usersResponse.current_page}
          totalPages={usersResponse.total_pages}
          totalItems={usersResponse.total}
          pageSize={usersResponse.page_size || pageSize}
          hasPrevious={usersResponse.has_previous}
          hasNext={usersResponse.has_next}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          className="mt-4"
        />
      )}
      <AddUserModal
        isOpen={addUserModalOpen}
        roleOptions={roleOptions}
        accountTeamRoleOptions={accountTeamRoleOptions}
        toggle={() => setAddUserModalOpen(false)}
        organizations={[]}
        loading={isEditMode ? updateUserLoading : createUserLoading}
        onSubmit={handleEditSubmit}
        editData={
          isEditMode && selectedUser
            ? {
                fullName: getUserDisplayName(selectedUser),
                email: selectedUser.email,
                role: selectedUser.role_name,
                position: selectedUser.position ?? "",
                phone: selectedUser.phone ?? "",
                city: selectedUser.city ?? "",
                country: selectedUser.country ?? "",
                join_date: selectedUser.join_date ?? "",
                languages: selectedUser.secondary_role ?? "",
                send_invitation: false,
              }
            : null
        }
        isEditMode={isEditMode}
      />
      {resetPasswordModalOpen && (
        <ResetPasswordModal
          isOpen={resetPasswordModalOpen}
          toggle={() => setResetPasswordModalOpen(false)}
          userName={selectedUser?.name || ""}
          onSubmit={handleResetPassword}
          loading={resetPasswordLoading}
        />
      )}
      <DeleteModal
        isOpen={deleteUserModalOpen}
        toggle={() => setDeleteUserModalOpen(false)}
        title="Delete User"
        entityName={getUserDisplayName(selectedUser)}
        entityDetails={selectedUser?.email ?? ""}
        warningItems={["User account and profile", "All associated data"]}
        onConfirm={handleDeleteConfirm}
        loading={deleteUserLoading}
      />

      <ToggleUserStatusModal
        isOpen={toggleStatusModalOpen}
        toggle={() => setToggleStatusModalOpen(false)}
        userName={getUserDisplayName(selectedUser)}
        currentStatus={selectedUser?.status || ""}
        onConfirm={handleToggleStatusConfirm}
        loading={updateUserLoading}
      />
      <CommonModal
        isOpen={toggleAccessModalOpen}
        toggle={handleToggleAccessCancel}
        title={
          pendingAccessToggle?.field === "executive_ops"
            ? pendingAccessToggle.newValue
              ? "Enable Exec Ops Access"
              : "Disable Exec Ops Access"
            : pendingAccessToggle?.newValue
              ? "Enable Approver Access"
              : "Disable Approver Access"
        }
        size="md"
      >
        <div className="toggle-user-status-modal">
          <p className="confirmation-text">
            {pendingAccessToggle?.field === "executive_ops_approve" &&
            pendingAccessToggle?.newValue &&
            !(pendingAccessToggle?.user?.executive_ops === true) ? (
              <>
                Enabling Approver Access will also enable Exec Ops Access. Are
                you sure you want to enable <strong>both</strong> for{" "}
                <strong>{getUserDisplayName(pendingAccessToggle?.user)}</strong>
                ?
              </>
            ) : (
              <>
                Are you sure you want to{" "}
                {pendingAccessToggle?.newValue ? "enable" : "disable"}{" "}
                <strong>
                  {pendingAccessToggle?.field === "executive_ops"
                    ? "Exec Ops Access"
                    : "Approver Access"}
                </strong>{" "}
                for{" "}
                <strong>{getUserDisplayName(pendingAccessToggle?.user)}</strong>
                ?
              </>
            )}
          </p>
          <div className="modal-actions">
            <Button variant="secondary" onClick={handleToggleAccessCancel}>
              Cancel
            </Button>
            <Button
              loading={updateUserLoading}
              variant="primary"
              onClick={handleToggleAccessConfirm}
            >
              {pendingAccessToggle?.field === "executive_ops_approve" &&
              pendingAccessToggle?.newValue &&
              !(pendingAccessToggle?.user?.executive_ops === true)
                ? "Enable Both"
                : pendingAccessToggle?.newValue
                  ? "Enable"
                  : "Disable"}
            </Button>
          </div>
        </div>
      </CommonModal>
      <CommonModal
        isOpen={toggleWeeklyDigestModalOpen}
        toggle={handleToggleWeeklyDigestCancel}
        title={
          pendingWeeklyDigestToggle?.newValue
            ? "Enable Weekly Digest"
            : "Disable Weekly Digest"
        }
        size="md"
      >
        <div className="toggle-user-status-modal">
          <p className="confirmation-text">
            Are you sure you want to{" "}
            {pendingWeeklyDigestToggle?.newValue ? "enable" : "disable"}{" "}
            <strong>Weekly Digest</strong> for{" "}
            <strong>
              {getUserDisplayName(pendingWeeklyDigestToggle?.user)}
            </strong>
            ?
          </p>
          <div className="modal-actions">
            <Button
              variant="secondary"
              onClick={handleToggleWeeklyDigestCancel}
            >
              Cancel
            </Button>
            <Button
              loading={updateUserLoading}
              variant="primary"
              onClick={handleToggleWeeklyDigestConfirm}
            >
              {pendingWeeklyDigestToggle?.newValue ? "Enable" : "Disable"}
            </Button>
          </div>
        </div>
      </CommonModal>
      <SendInvitationModal
        isOpen={sendInvitationModalOpen}
        toggle={() => setSendInvitationModalOpen(false)}
        userName={selectedUser?.name || ""}
        userEmail={selectedUser?.email || ""}
        onConfirm={handleSendInvitationConfirm}
        loading={resendWelcomeEmailLoading}
      />
      <SuccessNotification
        isOpen={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        message={notification.message}
        details={notification.details}
        type={notification.type}
      />
    </>
  );
};

export default VendorUserManagementContainer;
